import Frontend from "tm:frontend";
import { beginConversation } from "@/frontend/lib/conversation";
import { RPCClient } from "@/utils/rpc/client";
import { Analytics, AnalyticsDescription } from "./v4_analytics";
import { tr } from "@/utils/translations";
import GuardManager from "@/utils/guards";


interface AnalyticsTarget {
    setReferrer(referrer: string): Promise<void>;
    activate(id: string): Promise<void>
    event(code: string, params: Record<string, any>): Promise<void>;
}

interface AnalyticsContainer {
    iframe: HTMLIFrameElement;
    client: RPCClient;
    wrapper: AnalyticsTarget;
    port: MessagePort;
}

let __iframe: Promise<AnalyticsContainer>|null = null;
function getIFrame(): Promise<AnalyticsContainer> {
    if (__iframe !== null) return __iframe.then(i => {
        if (__iframe === null) throw new Error("IFrame closed while we were waiting.");
        return i;
    });

    __iframe = (async () => {
        GuardManager.addGuard("cookie");
        const httpPort = !!window.location.port ? `:${window.location.port}` : "";
        const origin = `${window.location.protocol}//wa-${Frontend.pid}.${Frontend.base_domain}${httpPort}`;

        const iframe = document.createElement("iframe");
        iframe.setAttribute("src", `${origin}${window.location.pathname}${window.location.search}${window.location.hash}`);
        iframe.setAttribute("aria-hidden", "true");
        iframe.setAttribute("title", "TicketMachine");
        iframe.setAttribute("sandbox", "allow-scripts allow-same-origin allow-forms allow-popups allow-modals");
        iframe.style.position = "absolute";
        iframe.style.top = "0px";
        iframe.style.left = "10000000px";
        iframe.style.border = "0";
        iframe.style.width = "0px";
        iframe.style.height = "0px";
        iframe.style.zIndex = "-1";
        iframe.style.opacity = "0";
        iframe.style.pointerEvents = "none";
        while (document.body === null) {
            await new Promise(rs => setTimeout(rs, 1));
        }
        document.body.appendChild(iframe);

        console.debug("[Analytics] Connecting.");
        const port = await beginConversation(iframe, origin);
        console.info("[Analytics] Got a connection.");
        const client = new RPCClient(port);
        const wrapper = {
            ...client.build((b) => b.declare("setReferrer").declare("activate").declare("event")) as any as AnalyticsTarget,
            close() { port.close(); }
        }

        await wrapper.setReferrer(document.referrer);

        return {
            iframe,
            port,
            client,
            wrapper
        }
    })();
    return __iframe!;
}

async function closeIFrame() {
    if (__iframe === null) return;
    if (!reloadKey) {
        GuardManager.addGuard("cookie");
        reloadKey = requestAnimationFrame(() => {
            reloadKey = null;
            GuardManager.removeGuard("cookie");
            if (activated.size === 0) return;

            (async () => {
                const wrapper = (await getIFrame()).wrapper;
                await Promise.all(Array.from(activated).map(i => wrapper.activate(i)));
            })().catch(window.reportError);
        });
    }
    const myiframe = __iframe;
    __iframe = null;
    const data = (await myiframe)!;
    data.port.close();
    data.iframe.remove();
    GuardManager.removeGuard("cookie");
}


const activated: Set<string> = new Set();
let reloadKey: any|null = null;

let _reloading: Promise<void>|null = null;
async function reloader(f: () => Promise<void>) {
    if (_reloading === null) {
        _reloading = f();
        _reloading.then(() => {_reloading = null;}, () => {_reloading = null;});
    }
    await _reloading;

}

export default class IFrameAnalytics implements Analytics {
    private _id: string;

    constructor(id: string) {
        this._id = id;
    }

    get id(): string {
        return this.id;
    }

    async initialize() {
        activated.add(this._id);
        if (!reloadKey) {
            (await getIFrame()).wrapper.activate(this._id);
        }
    }

    async event(name: string, params: Record<string, any>) {
        (await getIFrame()).wrapper.event(name, params);
    }

    async reload() {
        reloader(async () => {
            if (!(await getIFrame()).iframe.isConnected) {
                await closeIFrame();
                (await getIFrame()).wrapper.activate(this._id);
            }
        });
    }

    async close() {
        activated.delete(this._id);
        await closeIFrame();
    }

    static async describe(id: string): Promise<AnalyticsDescription> {
        const { trackers } = (await import('tm:~load:{controller: "customized/api", action: "tracker_info"}')) as any;
        return {
            ...trackers.find((t: {id: string}) => t.id === id),
            description: tr("cookie-law.vendors.organizer.description")
        }
    }
}
